import {
  BadgeDollarSignIcon,
  SearchIcon,
  ShirtIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from "lucide-react";
import ImageCropped from "~/components/image-cropped";
import { useOutfitProductCtx } from "~/components/outfit-product/outfit-product-provider";
import { cn } from "~/lib/utils";
import { useEntityAction } from "~/components/providers/user-actions-provider";
import { Toggle } from "~/components/ui/toggle";
import { AddToListDropdown } from "~/components/lists/add-to-list";

interface PropsClassNames {
  className?: string;
}

interface PropsWithChildren {
  className?: string;
  children: React.ReactNode;
}

export const OutfitProductCard = ({
  className,
  children,
}: PropsWithChildren) => {
  const { search } = useOutfitProductCtx();

  return (
    <div
      className={cn(
        "relative shrink-0 cursor-pointer rounded-lg border",
        className,
      )}
      onClick={search}
    >
      {children}
    </div>
  );
};

export const OutfitProductImage = ({ className }: PropsClassNames) => {
  const { src, rect } = useOutfitProductCtx();

  return (
    <ImageCropped
      className={cn(
        "aspect-square w-44 rounded-md bg-muted sm:w-full",
        className,
      )}
      src={src}
      boundingBox={rect}
    />
  );
};

export const OutfitProductTags = () => {
  const { price, currency, title } = useOutfitProductCtx();

  return (
    <div className="absolute left-1 top-1 w-fit max-w-[90%] space-y-1">
      {price && (
        <Tag className="w-fit">
          <BadgeDollarSignIcon className="h-4 w-4" />
          <div>
            {currency.symbol}
            {price.toFixed(2)}
          </div>
        </Tag>
      )}
      <Tag>
        <ShirtIcon className="h-4 w-4 shrink-0" />
        <div className="truncate">{title}</div>
      </Tag>
    </div>
  );
};

export const Tag = ({ className, children }: PropsWithChildren) => {
  return (
    <div
      className={cn(
        "flex w-fit max-w-full items-center gap-1.5 px-3 py-1",
        "text-xs font-medium leading-5",
        "bg-background/50 text-secondary-foreground backdrop-blur-md",
        "rounded-full",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const OutfitProductActions = () => {
  const { id, search } = useOutfitProductCtx();
  const { reaction, takeAction } = useEntityAction(id, "OUTFIT_PRODUCT");

  return (
    <div className="absolute bottom-2 left-0 flex w-full items-center justify-center">
      <div
        className={cn(
          "flex flex-row items-center justify-center gap-2 rounded-full p-1 shadow-md",
          "bg-background/50 text-secondary-foreground backdrop-blur-md",
        )}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Toggle size="icon" className="h-8 w-8 rounded-full" onClick={search}>
          <SearchIcon className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="icon"
          pressed={reaction === "LIKE"}
          className="h-8 w-8 rounded-full"
          onPressedChange={() => takeAction("LIKE")}
        >
          <ThumbsUpIcon className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="icon"
          pressed={reaction === "DISLIKE"}
          className="h-8 w-8 rounded-full"
          onPressedChange={() => takeAction("DISLIKE")}
        >
          <ThumbsDownIcon className="h-4 w-4" />
        </Toggle>
        <AddToListDropdown id={id} type="OUTFIT_PRODUCT" />
      </div>
    </div>
  );
};
